/**
 * Deep link INTO the Request access dialog, so another app can send an operator
 * here with the form already filled in.
 *
 * Shape: `https://access-ops.ennabl.xyz/#/requests?access_type=imp-ai&subject=a@b.com`
 *
 * `access_type`, not `kind`: the Grants page already keys its ACTIVE TAB on
 * `?kind=` in the query string (`GrantsPage.readUrl`). Two different meanings
 * behind one parameter name — even in two different parts of the URL — is the
 * kind of thing that eventually gets copy-pasted into the wrong half.
 *
 * The parameters live AFTER the `#` deliberately, not before. The UI is
 * hash-routed anyway, and a fragment is never sent to the server — so the
 * subject, which is a real person's email address, stays out of CloudFront and
 * ALB access logs. Browser history still holds it; that is the residual cost and
 * it is the reason not to widen this to anything more sensitive than an address.
 *
 * Today's only caller is ennabl-ai's admin UI, which links here when its
 * impersonation gate refuses: the operator lands on a pre-filled request instead
 * of having to retype who they were trying to reach.
 */
import {ChipKind} from './aggregate';
import {normalizeImpAiSubject} from './imp-ai-subject';

export interface RequestPrefill {
    kind: ChipKind;
    /** For `imp-ai`: the subject to pre-fill. Normalized, or dropped if invalid. */
    subject?: string;
}

const KINDS: readonly ChipKind[] = ['imp-ai', 'data-access', 'imp-decisions'];

/**
 * Read a prefill out of the current hash, or null when there is nothing to act
 * on. Never throws: a hand-mangled link should open an empty dialog, not break
 * the page.
 */
export function readRequestPrefill(hash: string = window.location.hash): RequestPrefill | null {
    const q = hash.indexOf('?');
    if (q === -1) {
        return null;
    }
    let params: URLSearchParams;
    try {
        params = new URLSearchParams(hash.slice(q + 1));
    } catch {
        return null;
    }
    const kindRaw = params.get('access_type');
    const kind = KINDS.find(k => k === kindRaw);
    if (!kind) {
        return null;
    }
    const subjectRaw = params.get('subject') ?? params.get('email');
    // An invalid subject is dropped rather than pre-filled: a token the form
    // would immediately reject is worse than an empty field, because the operator
    // cannot tell whether WE mangled it or they typed it wrong.
    const subject = subjectRaw ? normalizeImpAiSubject(subjectRaw) ?? undefined : undefined;
    return {kind, ...(subject ? {subject} : {})};
}

/**
 * Strip the prefill parameters from the hash once consumed, so a reload — or a
 * later manual open of the dialog — does not silently re-apply a stale subject.
 * Uses `replaceState` to avoid adding a history entry.
 */
export function clearRequestPrefill(): void {
    const hash = window.location.hash;
    const q = hash.indexOf('?');
    if (q === -1) {
        return;
    }
    const base = hash.slice(0, q);
    window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}${base}`);
}
